// Upload with the attestation every file needs. Shows the borrower-data screen's
// findings (where, never what) and, for BlackWolf, a logged override.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileUp, ShieldAlert } from 'lucide-react';
import { ApiError, errorMessage } from '@/lib/api';
import { ACCEPT, ATTESTATION, MAX_BYTES, docTypeLabel, fmtBytes, uploadDocument, type DocType } from '@/lib/documents';
import { trackerKey } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/field';
import { Alert } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

export interface UploadTarget {
  taskId?: string;
  phaseId?: string;
  deliverableId?: string;
  /** A meeting record, such as minutes (BlackWolf only). */
  meetingId?: string;
  /** Vendor materials and demo notes (BlackWolf only). */
  vendorId?: string;
  /** New version of an existing document. */
  documentId?: string;
  /** What the dialog says it is filing against. */
  label?: string;
}

const ADMIN_TYPES: DocType[] = ['WORKING_PAPER', 'CLIENT_PROVIDED', 'VENDOR_MATERIAL', 'MEETING_RECORD', 'INTERNAL'];
const CONTRIBUTOR_TYPES: DocType[] = ['WORKING_PAPER', 'CLIENT_PROVIDED', 'VENDOR_MATERIAL', 'MEETING_RECORD'];

interface Finding {
  location: string;
  rule: string;
}

export function UploadDialog({
  program,
  target,
  initialFile,
  open,
  onClose,
  onUploaded,
}: {
  program: Program;
  target: UploadTarget;
  initialFile?: File | null;
  open: boolean;
  onClose: () => void;
  onUploaded?: (r: { id: string; version: number }) => void;
}) {
  const isAdmin = program.capabilities.includes('plan.manage');
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<DocType>(
    target.meetingId ? 'MEETING_RECORD' : target.vendorId ? 'VENDOR_MATERIAL' : 'WORKING_PAPER',
  );
  const [tags, setTags] = useState('');
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [findings, setFindings] = useState<{ list: Finding[]; canOverride: boolean } | null>(null);
  const [override, setOverride] = useState('');

  const isDeliverable = Boolean(target.deliverableId);
  const isVersion = Boolean(target.documentId) || isDeliverable;

  useEffect(() => {
    if (!open) return;
    setFile(initialFile ?? null);
    setTitle(initialFile ? initialFile.name.replace(/\.[^.]+$/, '') : '');
    setType(target.meetingId ? 'MEETING_RECORD' : target.vendorId ? 'VENDOR_MATERIAL' : 'WORKING_PAPER');
    setTags('');
    setAttested(false);
    setError(null);
    setFindings(null);
    setOverride('');
  }, [open, initialFile, target.meetingId, target.vendorId]);

  function pick(f: File | undefined | null) {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setError(`${f.name} is ${fmtBytes(f.size)}. Files are capped at 50 MB.`);
      return;
    }
    setError(null);
    setFindings(null);
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!file || !attested) return;
    setBusy(true);
    setError(null);
    const form = new FormData();
    form.set('attestation', ATTESTATION);
    if (target.taskId) form.set('taskId', target.taskId);
    if (target.phaseId) form.set('phaseId', target.phaseId);
    if (target.deliverableId) form.set('deliverableId', target.deliverableId);
    if (target.meetingId) form.set('meetingId', target.meetingId);
    if (target.vendorId) form.set('vendorId', target.vendorId);
    if (target.documentId) form.set('documentId', target.documentId);
    if (!isVersion) {
      form.set('title', title.trim() || file.name);
      form.set('type', type);
      if (tags.trim()) form.set('tags', tags);
      if (type === 'INTERNAL') form.set('internal', 'true');
    }
    if (findings && override.trim()) form.set('screenOverrideReason', override.trim());
    form.set('file', file);
    try {
      const r = await uploadDocument(program.id, form);
      toast.ok(r.version > 1 ? `Version ${r.version} filed.` : 'Document filed.');
      void qc.invalidateQueries({ queryKey: ['documents', program.id] });
      void qc.invalidateQueries({ queryKey: ['deliverables', program.id] });
      void qc.invalidateQueries({ queryKey: trackerKey(program.id) });
      void qc.invalidateQueries({ queryKey: ['task', program.id] });
      void qc.invalidateQueries({ queryKey: ['meetings', program.id] });
      void qc.invalidateQueries({ queryKey: ['vendors', program.id] });
      onUploaded?.(r);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'BORROWER_DATA') {
        const d = err.details as { findings: Finding[]; canOverride: boolean };
        setFindings({ list: d.findings, canOverride: d.canOverride });
      } else {
        setError(errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const types = isAdmin ? ADMIN_TYPES : CONTRIBUTOR_TYPES;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isVersion ? 'File a new version' : 'File a document'}
      description={target.label ? `Against: ${target.label}` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {findings?.canOverride ? (
            <Button variant="danger" loading={busy} disabled={override.trim().length < 10} onClick={() => submit()}>
              Upload anyway
            </Button>
          ) : (
            <Button loading={busy} disabled={!file || !attested || Boolean(findings)} onClick={() => submit()}>
              <FileUp /> File it
            </Button>
          )}
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="bad">{error}</Alert>}
        <button
          type="button"
          onClick={() => input.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            pick(e.dataTransfer.files[0]);
          }}
          className="flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border-strong px-4 py-6 text-center text-sm hover:border-ring hover:bg-surface-2/50"
        >
          {file ? (
            <>
              <span className="font-medium">{file.name}</span>
              <span className="text-xs text-muted">{fmtBytes(file.size)} · click to choose another</span>
            </>
          ) : (
            <>
              <FileUp className="size-5 text-muted" />
              <span>Drop a file here, or click to choose</span>
              <span className="text-xs text-muted">
                PDF, Word, Excel, PowerPoint, CSV, text, Markdown, PNG, JPG. Up to 50 MB.
              </span>
            </>
          )}
        </button>
        <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={(e) => pick(e.target.files?.[0])} />

        {!isVersion && (
          <>
            <Field label="Title" htmlFor="up-title">
              <Input id="up-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Type"
                htmlFor="up-type"
                hint={type === 'INTERNAL' ? 'Never shown to client roles.' : undefined}
              >
                <Select id="up-type" value={type} onChange={(e) => setType(e.target.value as DocType)}>
                  {types.map((t) => (
                    <option key={t} value={t}>
                      {docTypeLabel(t, program)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Tags" htmlFor="up-tags" hint="Comma separated.">
                <Input
                  id="up-tags"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="interviews, operations"
                />
              </Field>
            </div>
          </>
        )}

        {findings ? (
          <div className="flex flex-col gap-3">
            <Alert tone="bad" title="This file looks like it holds borrower or loan-level data">
              HELM holds program artifacts only. Remove these and upload again:
              <ul className="mt-1.5 list-disc pl-5 text-[13px]">
                {findings.list.map((f, i) => (
                  <li key={i}>
                    {f.location}: {f.rule}
                  </li>
                ))}
              </ul>
            </Alert>
            {findings.canOverride && (
              <Field
                label="Override reason (BlackWolf only, logged)"
                htmlFor="up-override"
                hint="For example: a blank column template from the vendor, with no rows."
              >
                <Textarea
                  id="up-override"
                  rows={2}
                  value={override}
                  onChange={(e) => setOverride(e.target.value)}
                  maxLength={500}
                />
              </Field>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-border bg-surface-2/60 px-3 py-2.5">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warn" />
            <Checkbox
              id="up-attest"
              checked={attested}
              onChange={setAttested}
              label={`${ATTESTATION}.`}
              hint="Required for every upload."
            />
          </div>
        )}
      </form>
    </Dialog>
  );
}
